import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import {
  CreateSimpleLineItemDocument,
  DeleteLineItemDocument,
  QuotePricingLineItemFragment,
  QuotePricingPageDocument,
  UpdateSimpleLineItemDocument,
} from 'generated/resources/graphql'
import debounce from 'lodash/debounce'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Header from './Header'
import Body from './_Body'

interface QuotePhasesTableProps {
  currency: string
  lineItems: QuotePricingLineItemFragment[]
  isProductQuote: boolean
  isDraftStatus: boolean
  isJobCatalogTreeEnabled: boolean
}
const QuotePhasesTable = ({
  currency,
  lineItems,
  isProductQuote,
  isDraftStatus,
  isJobCatalogTreeEnabled,
}: QuotePhasesTableProps) => {
  const { quoteId } = useParams()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const apolloClient = useApolloClient()
  const [standaloneItems, setStandaloneItems] = useState<QuotePricingLineItemFragment[]>(lineItems)

  useEffect(() => {
    setStandaloneItems(lineItems)
  }, [lineItems])
  const [createSimpleLineItem] = useMutation(CreateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [updateSimpleLineItem] = useMutation(UpdateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updateSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [deleteLineItem] = useMutation(DeleteLineItemDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  const updateSimpleLineItemRef = useRef(updateSimpleLineItem)
  useEffect(() => {
    updateSimpleLineItemRef.current = updateSimpleLineItem
  }, [updateSimpleLineItem])

  const standaloneItemsRef = useRef(standaloneItems)
  useEffect(() => {
    standaloneItemsRef.current = standaloneItems
  }, [standaloneItems])

  const itemToDeleteRef = useRef<{
    item: QuotePricingLineItemFragment
    index: number
  } | null>(null)

  const debouncedUpdate = useMemo(
    () =>
      debounce(async (itemId: number, updates: Partial<QuotePricingLineItemFragment>) => {
        const currentItem = standaloneItemsRef.current.find((item) => item.id === itemId)
        if (!currentItem) return

        await updateSimpleLineItemRef.current({
          variables: {
            id: itemId,
            discountPercentage: 0,
            description: updates.description ?? currentItem.description ?? '-',
            quantity: updates.quantity ?? currentItem.quantity ?? 0,
            unitPriceCents: updates.unitPriceCents ?? currentItem.unitPriceCents ?? 0,
          },
        })
      }, 1000),
    []
  )

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate])

  const updateQuoteTotalAmountCents = (items: QuotePricingLineItemFragment[]) => {
    const totalAmountCents = items.reduce((sum, item) => sum + (item.totalPriceCents || 0), 0)

    const cachedData = apolloClient.readQuery({
      query: QuotePricingPageDocument,
      variables: { id: Number(quoteId), companyId: Number(companyId) },
    })

    if (cachedData?.finance?.quote) {
      apolloClient.writeQuery({
        query: QuotePricingPageDocument,
        variables: { id: Number(quoteId), companyId: Number(companyId) },
        data: {
          ...cachedData,
          finance: {
            ...cachedData.finance,
            quote: {
              ...cachedData.finance.quote,
              totalAmountCents,
            },
          },
        },
      })
    }
  }
  const handleUpdateStandaloneItem = async (
    itemId: number,
    updates: Partial<QuotePricingLineItemFragment>
  ) => {
    const updatedItems = standaloneItems.map((item) =>
      item.id === itemId ? { ...item, ...updates } : item
    )
    setStandaloneItems(updatedItems)
    updateQuoteTotalAmountCents(updatedItems)
    debouncedUpdate(itemId, updates)
  }

  const handleAddStandaloneItem = (itemType: 'role' | 'simple') => {
    const tempId = Date.now() * -1
    const optimisticItem: QuotePricingLineItemFragment = {
      id: tempId,
      description: '-',
      quantity: 1,
      unitPriceCents: 0,
      totalPriceCents: 0,
      totalCostCents: 0,
      unitCostCents: 0,
      taxRatePercentage: 0,
      profitCents: 0,
      parentId: null,
      quantityType: 'units' as const,
      taxRateId: null,
      taxTypeId: null,
      taxTypeName: null,
      taxTypeType: null,
      type: itemType === 'role' ? 'role' : 'simple',
    }

    setStandaloneItems((prev) => [...prev, optimisticItem])

    if (itemType === 'simple') {
      createSimpleLineItem({
        variables: {
          description: '-',
          discountPercentage: 0,
          quantity: 1,
          sourceId: Number(quoteId),
          sourceType: 'quote',
          unitPriceCents: 0,
        },
      }).catch(() => {
        setStandaloneItems((prev) => prev.filter((item) => item.id !== tempId))
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.save_error'),
          time: 2000,
        })
      })
    } else {
      // TODO: Execute createRoleLineItem
    }
  }

  const handleDeleteStandaloneItem = async (itemId: number) => {
    // Optimistic update: remove item immediately
    const itemIndex = standaloneItems.findIndex((item) => item.id === itemId)
    const itemToDelete = itemIndex >= 0 ? standaloneItems[itemIndex] : null

    // Store item and index for potential rollback
    if (itemToDelete) {
      itemToDeleteRef.current = {
        item: itemToDelete,
        index: itemIndex,
      }
    }

    const updatedItems = standaloneItems.filter((item) => item.id !== itemId)
    setStandaloneItems(updatedItems)
    updateQuoteTotalAmountCents(updatedItems)

    const result = await deleteLineItem({
      variables: {
        id: itemId,
      },
    })
    const handled = handleResult(result, () => undefined)

    const pendingToDelete = itemToDeleteRef.current
    itemToDeleteRef.current = null

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.deleted_successfully'),
        time: 2000,
      })
    } else {
      // Revert optimistic delete on error
      if (pendingToDelete) {
        const revertedItems = [...standaloneItems]
        revertedItems.splice(pendingToDelete.index, 0, pendingToDelete.item)
        setStandaloneItems(revertedItems)
        updateQuoteTotalAmountCents(revertedItems)
      }
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  return (
    <>
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        paddingBottom="s20"
      >
        <Text size="400" weight="semibold" color="grey1000">
          {i18n.t('finance.quotes.quote_phases_table.title')}
        </Text>
      </Box>
      <Table autoWidth>
        <Header isDraftStatus={isDraftStatus} isProductQuote={isProductQuote} />
        <Body
          standaloneItems={standaloneItems}
          currency={currency}
          isDraftStatus={isDraftStatus}
          isProductQuote={isProductQuote}
          isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
          onUpdateStandaloneItem={handleUpdateStandaloneItem}
          onAddStandaloneItem={handleAddStandaloneItem}
          onDeleteStandaloneItem={handleDeleteStandaloneItem}
        />
      </Table>
    </>
  )
}

export default QuotePhasesTable
